import {
  buildCreatedGoalLinkWrites,
  getNewGoalLinkFlags,
} from '@griply/services';
import {
  ActionEvents,
  GoalFormLinks,
  TrackingImageSources,
} from '@griply/types';
import {
  goalMetricIdToTrackingMetricTemplate,
  lifeAreaToTrackingLifeAreaType,
} from '@griply/utils';
import { useCallback, useRef } from 'react';
import { useCreateGoalMutation } from 'shared/hooks/queries/use-create-goal-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { NewGoal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';

import { useUpdateGoal } from './use-update-goal';

export type CreateGoalOptions = {
  lifeArea?: LifeArea;
  links: GoalFormLinks;
};

export const useCreateGoal = () => {
  const pendingRef = useRef<{ goal: NewGoal; links: GoalFormLinks }>();

  const track = useTrackEvents();
  const { mutateAsync, error, isPending, isSuccess, reset } =
    useCreateGoalMutation();
  const { submit: updateGoal } = useUpdateGoal();
  const updateRelationships = useUpdateRelationshipsReferences();

  // The create write carries the migrated flags; the relationship doc follows once the id exists.
  const createWithLinks = useCallback(
    async (goal: NewGoal, links: GoalFormLinks) => {
      const createdGoal = await mutateAsync({
        ...goal,
        ...getNewGoalLinkFlags(links),
      });
      const relationshipUpdates = buildCreatedGoalLinkWrites(
        createdGoal.id,
        links,
      );
      relationshipUpdates.length && updateRelationships(relationshipUpdates);
      return createdGoal;
    },
    [mutateAsync, updateRelationships],
  );

  const submit = useCallback(
    async (goal: NewGoal, { lifeArea, links }: CreateGoalOptions) => {
      const isSubGoal = !!links.parentIds.length;

      links.parentIds.forEach((parentId) => {
        updateGoal({
          id: parentId,
          showRoadmap: true,
        });
      });

      track(isSubGoal ? ActionEvents.SubGoalCreate : ActionEvents.GoalCreate, {
        life_area: lifeAreaToTrackingLifeAreaType(lifeArea),
        image_added: !!goal.image,
        image_source: goal.image
          ? goal.image?.metadata?.source === TrackingImageSources.Unsplash
            ? TrackingImageSources.Unsplash
            : TrackingImageSources.Custom
          : undefined,
        deadline: !!goal.deadline,
        target: goalMetricIdToTrackingMetricTemplate(goal.metric?.id),
      });

      pendingRef.current = { goal, links };
      return createWithLinks(goal, links);
    },
    [createWithLinks, track, updateGoal],
  );

  const retry = useCallback(() => {
    if (pendingRef.current) {
      createWithLinks(pendingRef.current.goal, pendingRef.current.links);
    }
  }, [createWithLinks]);

  const resetAll = useCallback(() => {
    reset();
    pendingRef.current = undefined;
  }, [reset]);

  return {
    submit,
    retry,
    reset: resetAll,
    error,
    isLoading: isPending,
    isSuccess,
  };
};
